import React, { useCallback, useEffect, useRef, useState } from "react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { ensureHttpsProtocol } from "simplydo/core/linkHref";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Input } from "simplydo/react/ui/input";
import { Popup } from "simplydo/react/ui/popup";

const HelpText = styled.p`
  margin: 0 0 5px 0;
`;

type LinkChooserProps = {
  trigger: React.ReactElement;
  isOpen: boolean;
  onComplete: (text: string, link: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  selectedText?: string;
  shouldFix?: boolean;
  hideLinkText?: boolean;
};

export const LinkChooser = ({
  trigger,
  isOpen,
  onComplete,
  onOpen,
  onClose,
  selectedText,
  shouldFix = true,
  hideLinkText = false,
}: LinkChooserProps) => {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState("");
  const [linkText, setLinkText] = useState("");
  const urlInputRef = useRef<HTMLInputElement>(null);

  const linkChosen = useCallback(() => {
    const fixedLink = shouldFix ? ensureHttpsProtocol(link) : link;
    onComplete(linkText, fixedLink);
    setOpen(false);
    setLink("");
    setLinkText("");
  }, [shouldFix, link, linkText, onComplete]);

  useEffect(() => {
    if (isOpen !== undefined && open !== isOpen) {
      setOpen(isOpen);
    }
  }, [open, isOpen]);

  useEffect(() => {
    if (selectedText) setLinkText(selectedText);
  }, [selectedText]);

  const togglePopup = () => {
    if (!open && onOpen) onOpen();
    if (open && onClose) onClose();
    setOpen(!open);
  };

  return (
    <Popup
      on="click"
      position="bottom left"
      open={open}
      trigger={React.cloneElement(trigger, {
        onClick: togglePopup,
      } as React.HTMLAttributes<HTMLElement>)}
    >
      {
        <div style={{ display: "flex", flexDirection: "column" }}>
          {hideLinkText ? null : (
            <>
              <HelpText>Enter the text to be linked.</HelpText>
              <Input
                disabled={!!selectedText}
                fluid
                value={linkText}
                onChange={(_e, data) => setLinkText(String(data.value ?? ""))}
                placeholder="Link text"
                style={{ marginBottom: 10 }}
              />
            </>
          )}
          <HelpText>Enter or paste a URL to create your link.</HelpText>
          <Input
            ref={urlInputRef}
            fluid
            value={link}
            onKeyDown={handleClickForKeyboard(linkChosen, { keys: ["Enter"] })}
            onChange={(_e, data) => setLink(String(data.value ?? ""))}
            placeholder="https://..."
            style={{ marginBottom: 10 }}
          />
          <div style={{ display: "flex" }}>
            <Button
              fluid
              onClick={linkChosen}
              disabled={link === ""}
              color="primary"
            >
              Insert link
            </Button>
            <Button
              onClick={() => {
                onClose?.();
              }}
            >
              Close
            </Button>
          </div>
        </div>
      }
    </Popup>
  );
};
